import moment from 'moment';

import cloneDeep from 'lodash/cloneDeep';

import { getFormValues } from 'redux-form';
import { handleActions } from 'redux-actions';

import endpoints from '../../../../config/endpoints';
import http from '../../../shared/http';

import createFilters, { getFiltersObject } from '../../../shared/filters';
import createCalendar, { getDateRangeFromCalendar } from '../../../shared/calendar';
import createPagination from '../../../shared/new-pagination';

export const namespace = 'manualMailingsList';

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'languages',
			title: 'Languages',
			placeholder: 'Add languages',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.languages'
			}
		},
		{
			name: 'statuses',
			title: 'Statuses',
			placeholder: 'Add statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: [{ value: 'sent', label: 'Sent' }, { value: 'not_sent', label: 'Not sent' }]
			}
		}
	]
});

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const pagination = createPagination({
	namespace,
	initialState: { perPage: 50 }
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_MORE_SUCCESS = `${namespace}/fetch-more-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const REMOVE_SUCCESS = `${namespace}/remove-success`;
const SET_SEARCH_QUERY = `${namespace}/set-search-query`;
const TOGGLE_IS_UNIQUE_EVENTS_SHOWED = `${namespace}/toggle-is-unique-events-showed`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

// initial state
const initialState = {
	data: null,
	isLoading: true,
	isError: false,
	filters: filters.initialState,
	search: null,
	isUniqueEventsShowed: true,
	calendar: calendar.initialState,
	pagination: pagination.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			const page = 1;
			request(getRequestQuery(page, getState), FETCH_DATA_SUCCESS)(dispatch)
				.then((action) => {
					if (action.type === FETCH_DATA_SUCCESS) {
						dispatch(pagination.actions.update({
							page,
							total: action.payload.data.total
						}));
					}
				});
		},
		fetchMore: () => (dispatch, getState) => {
			const page = getState()[namespace].pagination.currentPage + 1;
			request(getRequestQuery(page, getState), FETCH_MORE_SUCCESS)(dispatch)
				.then((action) => {
					if (action.type === FETCH_MORE_SUCCESS) {
						dispatch(pagination.actions.update({
							page,
							total: action.payload.data.total
						}));
					}
				});
		},
		removeBySplitTestId: (split_test_id) => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());

			const request = http({
				url: `${endpoints.MAILINGS_NEW.MANUAL_MAILINGS}/${split_test_id}`,
				method: 'delete',
				actions: [REMOVE_SUCCESS, FETCH_DATA_ERROR]
			});

			request(dispatch).then((action) => {
				if (action.type === REMOVE_SUCCESS) {
					location.reload();
				}
			});
		},
		search: () => (dispatch, getState) => {
			const searchForm = getFormValues(`${namespace}SearchForm`)(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({ type: SET_SEARCH_QUERY, payload: search });

			dispatch(actions.own.fetchData());
		},
		toggleIsUniqueEventsShowed: () => (dispatch, getState) => {
			dispatch({ type: TOGGLE_IS_UNIQUE_EVENTS_SHOWED });
		},
		apply: () => (dispatch, getState) => {
			dispatch(actions.own.fetchData());
		},
		clear: () => (dispatch, getState) => {
			dispatch({ type: CLEAR });
			dispatch(actions.own.fetchData());
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	},
	filters: filters.actions,
	calendar: calendar.actions,
	pagination: {
		showMore: () => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());
			dispatch(actions.own.fetchMore());
		}
	}
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			isLoading: false,
			isError: false
		}),
		[FETCH_MORE_SUCCESS]: (state, action) => ({
			...state,
			data: state.data.concat(action.payload.data.rows),
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[SET_SEARCH_QUERY]: (state, action) => ({
			...state,
			search: action.payload
		}),
		[TOGGLE_IS_UNIQUE_EVENTS_SHOWED]: (state, action) => ({
			...state,
			isUniqueEventsShowed: !state.isUniqueEventsShowed
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			search: action.payload,
			isLoading: false
		}),
		[CLEAR]: (state, action) => initialState,
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		})
	},
	filters.reducers,
	calendar.reducers,
	pagination.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state),
	getPagination: pagination.selectors.getPagination
};

function getRequestQuery(page, getState) {
	const state = getState()[namespace];

	const limit = state.pagination.perPage;
	const search = state.search;
	const range = getDateRangeFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		offset: (page - 1) * limit,
		limit,
		search,
		dates: [range.start, range.end],
		filters,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: endpoints.MAILINGS_NEW.MANUAL_MAILINGS,
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/mailings-new/manual-mailings/list/index.js